import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  DesignV2Scope,
  Field,
  Stepper,
  StickyBottomActions,
} from "../design-v2";
import { StackHeader } from "../components/product";
import { useManualProgram } from "../context/ManualProgramContext";
import { createWeeklyPlanDraft } from "../services/api";
import { buildOrigin, resolveBackTarget } from "../features/weeklyPlans/navigation";
import { getManualBuilderPath } from "../features/weeklyPlans/routes";
import "./manualNewProgram.css";

const MIN_SESSIONS_PER_WEEK = 1;
const MAX_SESSIONS_PER_WEEK = 7;

function DumbbellIcon() {
  return (
    <svg className="lz-manual-new__note-icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3 9v6M6 7v10M18 7v10M21 9v6M6 12h12" />
    </svg>
  );
}

export default function ManualNewProgram() {
  const navigate = useNavigate();
  const location = useLocation();
  const { hydrateProgramDraft } = useManualProgram();
  const [programName, setProgramName] = useState("");
  const [sessionsPerWeek, setSessionsPerWeek] = useState(4);
  const [nameError, setNameError] = useState("");
  const [hasInteractedWithName, setHasInteractedWithName] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const trimmedProgramName = programName.trim();
  const isProgramNameEmpty = trimmedProgramName.length === 0;
  const canCreateProgram = !isProgramNameEmpty;

  const decrementSessions = () => {
    setSessionsPerWeek((prev) => Math.max(MIN_SESSIONS_PER_WEEK, prev - 1));
  };

  const incrementSessions = () => {
    setSessionsPerWeek((prev) => Math.min(MAX_SESSIONS_PER_WEEK, prev + 1));
  };

  const validateProgramName = () => {
    if (trimmedProgramName.length === 0) {
      return "Program name is required";
    }

    return "";
  };

  const handleProgramNameChange = (event) => {
    const nextValue = event.target.value;
    setProgramName(nextValue);

    if (nameError && nextValue.trim().length > 0) {
      setNameError("");
    }
  };

  const handleProgramNameBlur = () => {
    setHasInteractedWithName(true);
    setNameError(validateProgramName());
  };

  const handleCreate = async () => {
    setHasInteractedWithName(true);

    const nextNameError = validateProgramName();
    setNameError(nextNameError);

    if (nextNameError || !canCreateProgram) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await createWeeklyPlanDraft({
        name: trimmedProgramName,
        source: "manual",
        sessionsPerWeek,
        workouts: [],
      });

      hydrateProgramDraft(response, {
        originRoute: resolveBackTarget(location, "/program"),
      });
      navigate(getManualBuilderPath(), {
        state: {
          from: buildOrigin(location),
          returnTo: resolveBackTarget(location, "/program"),
        },
      });
    } catch (error) {
      setNameError(error.message || "Unable to create program");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DesignV2Scope className="lz-manual-new" data-testid="manual-new-root">
      <StackHeader
        title="New Program"
        backLabel="Back"
        onBack={() => navigate(resolveBackTarget(location, "/program"))}
      />

      <main className="lz-manual-new__content">
        <Field
          className="lz-manual-new__name-field"
          label="Program Name"
          required
          error={hasInteractedWithName && nameError ? nameError : undefined}
        >
          <input
            type="text"
            autoFocus
            value={programName}
            onChange={handleProgramNameChange}
            onBlur={handleProgramNameBlur}
            placeholder="Name your program"
          />
        </Field>

        <section className="lz-manual-new__sessions" aria-labelledby="manual-new-sessions-title">
          <div className="lz-manual-new__sessions-heading">
            <h2 id="manual-new-sessions-title">Sessions Per Week</h2>
            <p>Number of workouts in your weekly template</p>
          </div>

          <Card className="lz-manual-new__sessions-card">
            <Stepper
              label="Sessions per week"
              className="lz-manual-new__stepper"
              value={sessionsPerWeek}
              unit={sessionsPerWeek === 1 ? "Workout" : "Workouts"}
              onDecrement={decrementSessions}
              onIncrement={incrementSessions}
              canDecrement={sessionsPerWeek > MIN_SESSIONS_PER_WEEK}
              canIncrement={sessionsPerWeek < MAX_SESSIONS_PER_WEEK}
              decrementLabel="Decrease sessions"
              incrementLabel="Increase sessions"
            />

            <div className="lz-manual-new__sessions-track" aria-hidden="true">
              {Array.from({ length: MAX_SESSIONS_PER_WEEK }).map((_, index) => (
                <span
                  className="lz-manual-new__sessions-tick"
                  data-filled={index < sessionsPerWeek ? "true" : "false"}
                  key={index}
                />
              ))}
            </div>
          </Card>
        </section>

        <Card className="lz-manual-new__note" tone="raised">
          <span className="lz-manual-new__note-mark">
            <DumbbellIcon />
          </span>
          <div className="lz-manual-new__note-copy">
            <h3>Manual Mode: Workout Plan</h3>
            <p>Define your own split, sets, and rep ranges from scratch.</p>
          </div>
        </Card>
      </main>

      <StickyBottomActions className="lz-manual-new__actions">
        <Button
          className="lz-manual-new__submit"
          size="lg"
          onClick={handleCreate}
          disabled={!canCreateProgram}
          isLoading={isSubmitting}
          loadingLabel="Creating..."
        >
          Create Weekly Template
        </Button>
      </StickyBottomActions>
    </DesignV2Scope>
  );
}
